/**
 * パネルのレイアウト設定を取得する関数
 * @param isPaidPlan 有料プランフラグ（紹介特典/Stripe含む）
 * @param showReferralDeskPanel デスクで紹介パネルを表示するか（紹介特典に紐づくトライアル中のみ true など）
 */
export const getPanelLayouts = (isPaidPlan: boolean, showReferralDeskPanel = false) => ({
    /**
     * PCレイアウト
     * 最大カラム数１２
     */
    pc: [
        /**
         * デスク画面
         */
        { i: 'hmActivityCalendar', x: 0, y: 1, w: 6, h: 3, minW: 2, minH: 3 },
        { i: 'hmTimeClock', x: 6, y: 1, w: 3, h: 3, minW: 3, minH: 2 },
        { i: 'hmTimeProgress', x: 6, y: 0, w: 12, h: 1, minW: 2, minH: 1 },
        {
            i: 'hmSimpleAnalytics',
            x: 0,
            y: 4,
            w: 6,
            h: 3,
            minW: 3,
            minH: 3,
            // 無料プラン以外はサイズ上限なし
            maxH: isPaidPlan ? undefined : 3,
            maxW: isPaidPlan ? undefined : 6,
        },
        { i: 'hmMemo', x: 0, y: 7, w: 6, h: 3, minW: 3, minH: 2 },
        // 紹介パネルは紹介成立後など、紹介特典に紐づくトライアル中のみ表示
        ...(showReferralDeskPanel
            ? [
                  {
                      i: 'hmReferral',
                      x: 9,
                      y: 7,
                      w: 6,
                      h: 3,
                      minW: 3,
                      minH: 3,
                      isDraggable: false,
                      isResizable: false,
                  },
              ]
            : []),
        // 広告は「無料（有料プランでない）」時のみ表示
        ...(!isPaidPlan
            ? [
                  {
                      i: 'hmAd',
                      x: 9,
                      y: 1,
                      w: 3,
                      h: 3,
                      minW: 3,
                      minH: 3,
                      isDraggable: false,
                      isResizable: false,
                  },
              ]
            : []),
        { i: 'hmAppRegistIntro', x: 0, y: 10, w: 6, h: 3 },
        /**
         * 分析画面（検索・期間はグリッド外ヘッダー。ここはスコアカード以降のみ）
         */
        {
            i: 'anTotalTime',
            x: 0,
            y: 0,
            w: 3,
            h: 2,
            minW: 2,
            minH: 2,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        {
            i: 'anAverageTime',
            x: 3,
            y: 0,
            w: 3,
            h: 2,
            minW: 2,
            minH: 2,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        {
            i: 'anTotalSchedule',
            x: 6,
            y: 0,
            w: 3,
            h: 2,
            minW: 2,
            minH: 2,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        {
            i: 'anRanking',
            x: 7,
            y: 2,
            w: 5,
            h: 3,
            minW: 3,
            minH: 2,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        {
            i: 'anChart',
            x: 0,
            y: 2,
            w: 7,
            h: 3,
            minW: 2,
            minH: 2,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        {
            i: 'anTable',
            x: 0,
            y: 5,
            w: 12,
            h: 7,
            minW: 5,
            minH: 4,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        // 有料プランの場合は広告を非表示（紹介特典も有料扱いで非表示）
        ...(!isPaidPlan
            ? [
                  {
                      i: 'anAd',
                      x: 9,
                      y: 0,
                      w: 3,
                      h: 2,
                      minW: 2,
                      minH: 2,
                      static: true,
                      isDraggable: false,
                      isResizable: false,
                  },
              ]
            : []),
    ],
    /**
     * タブレットレイアウト
     * 最大カラム数６
     */
    tablet: [
        // デスク画面
        { i: 'hmTimeClock', x: 0, y: 0, w: 3, h: 3, minW: 3, minH: 3 },
        { i: 'hmActivityCalendar', x: 0, y: 3, w: 3, h: 3, minW: 3, minH: 3 },
        { i: 'hmTimeProgress', x: 3, y: 0, w: 3, h: 3, minW: 3, minH: 3 },
        { i: 'hmSimpleAnalytics', x: 0, y: 6, w: 3, h: 3, minW: 3, minH: 3 },
        { i: 'hmMemo', x: 3, y: 6, w: 3, h: 3, minW: 3, minH: 3 },
        ...(showReferralDeskPanel
            ? [
                  {
                      i: 'hmReferral',
                      x: 0,
                      y: 9,
                      w: 6,
                      h: 3,
                      minW: 3,
                      minH: 2,
                      isDraggable: false,
                      isResizable: false,
                  },
              ]
            : []),
        // 広告は「無料（有料プランでない）」時のみ表示
        ...(!isPaidPlan
            ? [
                  {
                      i: 'hmAd',
                      x: 3,
                      y: 3,
                      w: 3,
                      h: 3,
                      minW: 3,
                      minH: 3,
                      isDraggable: false,
                      isResizable: false,
                  },
              ]
            : []),
        { i: 'hmAppRegistIntro', x: 0, y: 26, w: 6, h: 3 },
        // 分析画面（検索・期間はグリッド外）
        {
            i: 'anTotalTime',
            x: 0,
            y: 0,
            w: 2,
            h: 2,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        {
            i: 'anAverageTime',
            x: 2,
            y: 0,
            w: 2,
            h: 2,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        ...(isPaidPlan
            ? [
                  {
                      i: 'anTotalSchedule',
                      x: 4,
                      y: 0,
                      w: 2,
                      h: 2,
                      isDraggable: true,
                      isResizable: true,
                  },
                  {
                      i: 'anChart',
                      x: 0,
                      y: 2,
                      w: 4,
                      h: 4,
                      isDraggable: true,
                      isResizable: true,
                  },
                  {
                      i: 'anRanking',
                      x: 4,
                      y: 2,
                      w: 2,
                      h: 4,
                      isDraggable: true,
                      isResizable: true,
                  },
                  {
                      i: 'anTable',
                      x: 0,
                      y: 6,
                      w: 6,
                      h: 6,
                      minW: 2,
                      minH: 2,
                      isDraggable: true,
                      isResizable: true,
                  },
              ]
            : [
                  {
                      i: 'anTotalSchedule',
                      x: 0,
                      y: 2,
                      w: 4,
                      h: 2,
                      isDraggable: false,
                      isResizable: false,
                  },
                  {
                      i: 'anChart',
                      x: 0,
                      y: 4,
                      w: 4,
                      h: 4,
                      isDraggable: false,
                      isResizable: false,
                  },
                  {
                      i: 'anRanking',
                      x: 4,
                      y: 4,
                      w: 2,
                      h: 4,
                      isDraggable: false,
                      isResizable: false,
                  },
                  {
                      i: 'anTable',
                      x: 0,
                      y: 8,
                      w: 6,
                      h: 6,
                      minW: 2,
                      minH: 2,
                      isDraggable: false,
                      isResizable: false,
                  },
              ]),
        // 有料プランの場合は広告を非表示（紹介特典も有料扱いで非表示）
        ...(!isPaidPlan
            ? [
                  {
                      i: 'anAd',
                      x: 4,
                      y: 0,
                      w: 2,
                      h: 4,
                      minW: 2,
                      minH: 2,
                      isDraggable: false,
                      isResizable: false,
                  },
              ]
            : []),
    ],
    /**
     * モバイルレイアウト
     * 最大カラム数２
     */
    mobile: [
        // デスク画面
        {
            i: 'hmTimeClock',
            x: 0,
            y: 0,
            w: 2,
            h: 1,
            minH: 1,
            minW: 2,
            isDraggable: true,
            isResizable: true,
        },
        { i: 'hmTimeProgress', x: 0, y: 1, w: 2, h: 1, minH: 1, minW: 2, isDraggable: true, isResizable: true },
        { i: 'hmActivityCalendar', x: 0, y: 2, w: 2, h: 3, isDraggable: true, isResizable: true },
        { i: 'hmSimpleAnalytics', x: 0, y: 5, w: 2, h: 4, isDraggable: true, isResizable: true },
        { i: 'hmMemo', x: 0, y: 9, w: 2, h: 4, isDraggable: true, isResizable: true },
        // 紹介パネルは紹介成立後など、紹介特典に紐づくトライアル中のみ表示
        ...(showReferralDeskPanel
            ? [
                  {
                      i: 'hmReferral',
                      x: 0,
                      y: 13,
                      w: 2,
                      h: 4,
                      isDraggable: false,
                      isResizable: false,
                  },
              ]
            : []),
        // 広告は「無料（有料プランでない）」時のみ表示
        ...(!isPaidPlan
            ? [
                  {
                      i: 'hmAd',
                      x: 0,
                      y: 17,
                      w: 2,
                      h: 3,
                      static: true,
                      isDraggable: false,
                      isResizable: false,
                  },
              ]
            : []),
        {
            i: 'hmAppRegistIntro',
            x: 0,
            y: 20,
            w: 2,
            h: 3,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        // 分析画面（検索・期間はグリッド外）
        {
            i: 'anTotalTime',
            x: 0,
            y: 0,
            w: 2,
            h: 2,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        {
            i: 'anAverageTime',
            x: 0,
            y: 2,
            w: 1,
            h: 2,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        {
            i: 'anTotalSchedule',
            x: 1,
            y: 2,
            w: 1,
            h: 2,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        {
            i: 'anRanking',
            x: 0,
            y: 4,
            w: 2,
            h: 3,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        {
            i: 'anChart',
            x: 0,
            y: 7,
            w: 2,
            h: 3,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        {
            i: 'anTable',
            x: 0,
            y: 10,
            w: 2,
            h: 10,
            minW: 2,
            minH: 2,
            isDraggable: isPaidPlan,
            isResizable: isPaidPlan,
        },
        // 有料プランの場合は広告を非表示（紹介特典も有料扱いで非表示）
        ...(!isPaidPlan
            ? [
                  {
                      i: 'anAd',
                      x: 0,
                      y: 20,
                      w: 2,
                      h: 3,
                      minW: 2,
                      minH: 2,
                      static: true,
                      isDraggable: false,
                      isResizable: false,
                  },
              ]
            : []),
    ],
});
